import { useMemo, useState } from 'react';
import { localeTag } from '@elec/domain';
import { localeTag as uiTag, type Translate } from '@elec/i18n';
import {
  dxfToPlan,
  fittingScale,
  planSize,
  roomScanToPlan,
  unitMm,
  type DxfDrawing,
  type RoomScan,
  type VectorPlan,
} from '@elec/plan';
import { Dialog } from './ui.tsx';

/** After two clicks on the plan: enter the real distance (and the drawing scale). */
export function CalibrateDialog(props: {
  t: Translate;
  measuredMm: number;
  scale: number;
  onCancel(): void;
  onApply(meters: number, scale: number): void;
}) {
  const { t } = props;
  const [meters, setMeters] = useState('');
  const [scale, setScale] = useState(String(props.scale));
  const m = Number(meters.replace(',', '.'));
  const s = Number(scale);
  const ok = m > 0 && s > 0;
  return (
    <Dialog
      title={t('plan.calibrate').replace('…', '')}
      onClose={props.onCancel}
      actions={
        <>
          <button onClick={props.onCancel}>{t('dialog.cancel')}</button>
          <button
            className="primary"
            disabled={!ok}
            onClick={() => props.onApply(m, s)}
            data-testid="calibrate-ok"
          >
            {t('dialog.ok')}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (ok) props.onApply(m, s);
        }}
      >
        <p className="small muted">{t('plan.measured', { mm: props.measuredMm.toFixed(1) })}</p>
        <div className="field">
          <label htmlFor="cal-m">{t('plan.distance')}</label>
          <input
            id="cal-m"
            inputMode="decimal"
            value={meters}
            onChange={(e) => setMeters(e.target.value)}
            autoFocus
            data-testid="calibrate-meters"
          />
        </div>
        <div className="field">
          <label htmlFor="cal-s">{t('plan.scale')}</label>
          <select id="cal-s" value={scale} onChange={(e) => setScale(e.target.value)}>
            {[20, 50, 75, 100, 200].map((v) => (
              <option key={v} value={v}>
                1:{v}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

const UNITS: Array<{ mm: number; label: string }> = [
  { mm: 1, label: 'mm' },
  { mm: 10, label: 'cm' },
  { mm: 1000, label: 'm' },
  { mm: 25.4, label: 'inch' },
  { mm: 304.8, label: 'ft' },
];
const SCALES = [20, 25, 50, 75, 100, 125, 200, 250, 500];

/**
 * DXF import: which layers, the drawing unit (stated by the file or guessed) and the drawing
 * scale; the plan is placed at its true size on the sheet.
 */
export function DxfImportDialog(props: {
  t: Translate;
  locale: 'fr' | 'en' | 'nl';
  drawing: DxfDrawing;
  /** Drawing area of the sheet (mm). */
  area: { width: number; height: number };
  onCancel(): void;
  onImport(plan: VectorPlan, scale: number): void;
}) {
  const { t, drawing: d } = props;
  const unit = unitMm(d);
  const [mm, setMm] = useState(unit.mm);
  const [layers, setLayers] = useState<Set<string>>(
    () => new Set(d.layers.filter((l) => !l.hidden && l.lines + l.texts > 0).map((l) => l.name)),
  );
  const plan = useMemo(() => dxfToPlan(d, { layers, mmPerUnit: mm }), [d, layers, mm]);
  const [scale, setScale] = useState(() =>
    fittingScale(dxfToPlan(d, { layers, mmPerUnit: unit.mm }), props.area),
  );
  const { width, height } = planSize(plan);
  const empty = plan.layers.every((l) => !l.lines.length && !l.texts.length);
  const paper = { w: width / scale, h: height / scale };
  const tooBig = paper.w > props.area.width + 0.5 || paper.h > props.area.height + 0.5;
  const fmt = (v: number, digits = 1) =>
    v.toLocaleString(localeTag(props.locale), { maximumFractionDigits: digits });
  const sizeM = planSize(dxfToPlan(d, { layers: new Set(d.layers.map((l) => l.name)), mmPerUnit: mm }));
  const skipped = Object.entries(d.skipped)
    .map(([k, n]) => `${k} (${n})`)
    .join(', ');
  return (
    <Dialog
      title={t('dxf.title')}
      wide
      onClose={props.onCancel}
      actions={
        <>
          <button onClick={props.onCancel}>{t('dialog.cancel')}</button>
          <button
            className="primary"
            disabled={empty}
            onClick={() => props.onImport(plan, scale)}
            data-testid="dxf-import"
          >
            {t('dxf.import')}
          </button>
        </>
      }
    >
      <p>
        {t('dxf.info', {
          version: d.version,
          w: fmt(sizeM.width / 1000),
          h: fmt(sizeM.height / 1000),
        })}
      </p>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="dxf-unit">{t('dxf.units')}</label>
          <select
            id="dxf-unit"
            value={mm}
            onChange={(e) => setMm(Number(e.target.value))}
            data-testid="dxf-unit"
          >
            {UNITS.map((u) => (
              <option key={u.mm} value={u.mm}>
                {u.label}
              </option>
            ))}
          </select>
          <small className={unit.guessed ? 'warn' : 'muted'}>
            {unit.guessed ? t('dxf.unitsGuessed') : t('dxf.unitsStated')}
          </small>
        </div>
        <div className="field">
          <label htmlFor="dxf-scale">{t('dxf.scale')}</label>
          <select
            id="dxf-scale"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            data-testid="dxf-scale"
          >
            {SCALES.map((s) => (
              <option key={s} value={s}>
                1:{s}
              </option>
            ))}
          </select>
          <small className={tooBig ? 'warn' : 'muted'} data-testid="dxf-paper">
            {tooBig ? t('dxf.tooBig') : t('dxf.paperSize', { w: fmt(paper.w, 0), h: fmt(paper.h, 0) })}
          </small>
        </div>
      </div>
      <div className="dxf-layers">
        <div className="dxf-layers-head">
          <strong>{t('dxf.layers')}</strong>
          <button onClick={() => setLayers(new Set(d.layers.map((l) => l.name)))}>{t('dxf.all')}</button>
          <button onClick={() => setLayers(new Set())}>{t('dxf.none')}</button>
        </div>
        <ul data-testid="dxf-layers">
          {d.layers.map((l) => (
            <li key={l.name}>
              <label>
                <input
                  type="checkbox"
                  checked={layers.has(l.name)}
                  onChange={(e) => {
                    const next = new Set(layers);
                    if (e.target.checked) next.add(l.name);
                    else next.delete(l.name);
                    setLayers(next);
                  }}
                  data-layer={l.name}
                />
                <span className="dxf-layer-name">{l.name}</span>
                <span className="muted">
                  {t('dxf.layerCount', { n: l.lines + l.texts })}
                  {l.hidden && ` · ${t('dxf.hiddenLayer')}`}
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      <p className="muted small">
        {t('dxf.trueScale')}
        {d.strays > 0 && ` ${t('dxf.strays', { n: d.strays })}`}
        {skipped && ` ${t('dxf.skipped', { list: skipped })}`}
      </p>
      {empty && <p className="warn">{t('dxf.empty')}</p>}
    </Dialog>
  );
}

/** A room scan (iPhone / iPad LiDAR, Apple RoomPlan): floor, objects and scale, then the plan. */
export function RoomScanDialog(props: {
  t: Translate;
  locale: 'fr' | 'en' | 'nl' | 'de';
  scan: RoomScan;
  /** Drawing area of the sheet (mm). */
  area: { width: number; height: number };
  onCancel(): void;
  onImport(plan: VectorPlan, scale: number): void;
}) {
  const { t, scan } = props;
  const [story, setStory] = useState(scan.stories[0] ?? 0);
  const [objects, setObjects] = useState(true);
  const plan = useMemo(
    () => roomScanToPlan(scan, { story, objects, locale: props.locale }),
    [scan, story, objects, props.locale],
  );
  const [scale, setScale] = useState(() => fittingScale(plan, props.area));
  const { width, height } = planSize(plan);
  const paper = { w: width / scale, h: height / scale };
  const tooBig = paper.w > props.area.width + 0.5 || paper.h > props.area.height + 0.5;
  const fmt = (v: number, digits = 1) =>
    v.toLocaleString(uiTag(props.locale), { maximumFractionDigits: digits });
  const c = scan.counts;
  return (
    <Dialog
      title={t('scan.title')}
      onClose={props.onCancel}
      actions={
        <>
          <button onClick={props.onCancel}>{t('dialog.cancel')}</button>
          <button className="primary" onClick={() => props.onImport(plan, scale)} data-testid="scan-import">
            {t('dxf.import')}
          </button>
        </>
      }
    >
      <p data-testid="scan-summary">
        {t('scan.summary', {
          walls: c.walls,
          doors: c.doors,
          windows: c.windows,
          rooms: c.rooms,
          w: fmt(width / 1000),
          h: fmt(height / 1000),
        })}
      </p>
      <div className="form-grid">
        {scan.stories.length > 1 && (
          <div className="field">
            <label htmlFor="scan-story">{t('scan.story')}</label>
            <select
              id="scan-story"
              value={story}
              onChange={(e) => setStory(Number(e.target.value))}
              data-testid="scan-story"
            >
              {scan.stories.map((s) => (
                <option key={s} value={s}>
                  {t('scan.storyN', { n: s })}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label htmlFor="scan-scale">{t('dxf.scale')}</label>
          <select
            id="scan-scale"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            data-testid="scan-scale"
          >
            {SCALES.map((s) => (
              <option key={s} value={s}>
                1:{s}
              </option>
            ))}
          </select>
          <small className={tooBig ? 'warn' : 'muted'}>
            {tooBig ? t('dxf.tooBig') : t('dxf.paperSize', { w: fmt(paper.w, 0), h: fmt(paper.h, 0) })}
          </small>
        </div>
      </div>
      {c.objects > 0 && (
        <label className="check">
          <input
            type="checkbox"
            checked={objects}
            onChange={(e) => setObjects(e.target.checked)}
            data-testid="scan-objects"
          />
          <span>{t('scan.objects', { n: c.objects })}</span>
        </label>
      )}
      <p className="muted small">{t('scan.note')}</p>
    </Dialog>
  );
}
